import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Pill } from "../../components/common/Primitives";
import { Shield, RefreshCw } from "lucide-react";

/**
 * Admin session audit — every login is written by routes/auth.py into
 * user_sessions; this page renders that log for super_admin / admin / manager.
 */
export default function AdminSessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/sessions").then((r) => setRows(r.data?.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  return (
    <DashboardShell title="Sessions" subtitle="Admin · Login audit trail">
      <Toaster richColors position="top-right" />
      <div className="space-y-3" data-testid="admin-sessions">
        <div className="flex items-center justify-between">
          <div className="text-xs text-ink-muted flex items-center gap-1">
            <Shield size={12} /> Every login and Google OAuth session lands here (newest first).
          </div>
          <button onClick={load} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1" data-testid="sessions-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="text-sm italic text-ink-muted">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm italic text-ink-muted border border-navy/10 rounded p-6 text-center" data-testid="sessions-empty">
            No sessions recorded yet.
          </div>
        ) : (
          <div className="border border-navy/10 rounded bg-white/60 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-navy/[0.04] text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                <tr>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">IP</th>
                  <th className="text-left px-3 py-2">Device / UA</th>
                  <th className="text-left px-3 py-2">Login</th>
                  <th className="text-left px-3 py-2">Last used</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/[0.06]">
                {rows.map((s) => (
                  <tr key={s.session_id} className="hover:bg-navy/[0.02]" data-testid={`session-row-${s.session_id}`}>
                    <td className="px-3 py-2 font-heading text-ink">{s.user_email || s.user_id}</td>
                    <td className="px-3 py-2">{s.role || "—"}</td>
                    <td className="px-3 py-2">
                      <Pill tone={s.provider === "google" ? "gold" : "muted"}>{s.provider || s.auth_kind || "jwt"}</Pill>
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">{s.ip || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] truncate max-w-[200px]" title={s.user_agent}>
                      {s.user_agent ? s.user_agent.slice(0, 60) : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px]">{formatDateTime(s.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{formatDateTime(s.last_used_at || s.created_at)}</td>
                    <td className="px-3 py-2">
                      {s.revoked_at
                        ? <Pill tone="danger">Revoked</Pill>
                        : s.expires_at && new Date(s.expires_at) < new Date()
                          ? <Pill tone="muted">Expired</Pill>
                          : <Pill tone="success">Active</Pill>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
